<script setup lang="ts">
import { computed, ref } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { useAuthStore } from "@/stores/authStore";
import type { AxiosError } from "axios";
import BoomerangLogo from "@/components/BoomerangLogo.vue";
import type { LoginRequest } from "@/types/services/auth/auth.type";
import type { ApiError } from "@/types/services/utils/axios.type";
import type { ValidationRule } from "@/types/utils/validators.type.ts";
import { displayResponseError } from "@/utils/toastUtils";
import validators from "@/utils/validators.ts";

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

const formRef = ref();
const email = ref("");
const identifier = ref("");
const loading = ref(false);
const error = ref<string | null>(null);

const emailRules = ref<ValidationRule[]>([...validators.emailRules]);

const isFormReady = computed(() => {
  return email.value.trim() && identifier.value.trim();
});

const submit = async () => {
  const { valid } = await formRef.value.validate();
  if (!valid) return;

  if (!email.value.trim()) {
    error.value = t("views.loginView.emailError");
    return;
  }

  loading.value = true;
  error.value = null;

  const payload: LoginRequest = {
    identifier: identifier.value.trim(),
    email: email.value.trim(),
  };

  try {
    await authStore.login(payload);
    localStorage.setItem("email", email.value.trim());
    await router.push((route.query.redirect as string) || "/");
  } catch (error) {
    displayResponseError(error as AxiosError<ApiError>);
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <v-app>
    <v-main class="login-page">
      <div class="login-page__content">
        <div class="login-page__logo">
          <BoomerangLogo class="login-logo" />
        </div>

        <v-form ref="formRef" class="login-form" @submit.prevent="submit">
          <div class="field">
            <v-text-field
              v-model="email"
              :label="t('views.loginView.email')"
              :placeholder="t('views.loginView.emailPlaceholder')"
              :rules="emailRules"
              :disabled="loading"
              variant="outlined"
              density="comfortable"
              class="input-field-v"
            />
          </div>

          <div class="field">
            <v-text-field
              v-model="identifier"
              :label="t('views.loginView.identifier')"
              :placeholder="t('views.loginView.placeholder')"
              :disabled="loading"
              variant="outlined"
              density="comfortable"
              hide-details
              class="input-field-v"
            />
          </div>

          <v-alert
            v-if="error"
            type="error"
            variant="tonal"
            density="compact"
            class="mt-3"
          >
            {{ error }}
          </v-alert>

          <v-btn
            type="submit"
            block
            class="btn-primary-login mt-6"
            :loading="loading"
            :disabled="loading || !isFormReady"
            rounded="xl"
            height="52"
          >
            {{ t("views.loginView.submit") }}
          </v-btn>
        </v-form>
      </div>
    </v-main>
  </v-app>
</template>

<style scoped>
.login-page {
  min-height: 100dvh;
  position: relative;
  background: url("@/assets/login-bg.jpg") center / cover no-repeat;

  display: flex;
  justify-content: center;
  align-items: center;
  padding: 28px;
}

.login-page__content {
  width: 100%;
  max-width: 420px;
  background-color: white;
  padding: 24px;
  border-radius: 18px;
}

.login-page__logo {
  margin-bottom: 56px;
}

.login-form {
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.field__label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  color: hsl(var(--foreground));
}
</style>
